import { useAuth0, User } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { useAuthToken } from "../State/Hooks/authHooks";
import { IActivateUserRaw, IUser } from "../Typings/User";
import { apiPathNASUserEmail, fetcherNASAdmin } from "./queryHelpers";

const NAS_UserActivateId_Key = "http://nasrecruitment.io/activateId";

const initialAuth0User: User = {
  email: "",
  nickname: "",
};

const initialUser: IUser = {
  id: "",
  email: "",
  name: "",
  nickname: "",
  picture: "",
  firstName: "",
  lastName: "",
};

export function useActivateUserQuerySelector<T>(
  auth0User: User,
  select?: (data: IActivateUserRaw) => T | undefined,
) {
  const token = useAuthToken();

  return useQuery(
    ["user", auth0User.email],
    () =>
      fetcherNASAdmin(apiPathNASUserEmail + auth0User.email, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    {
      select,
      enabled: !!auth0User.email && !!token,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * Custom hook to fetch and manage the current user's data, including their base client access roles.
 *
 * @returns {IUser} The current user's information, including base clients with CRM access.
 */
export function useCurrentUser(): IUser {
  const { user } = useAuth0();
  const auth0User = user || initialAuth0User;
  const activateId = auth0User[NAS_UserActivateId_Key];

  const selectUsernameData = (data: IActivateUserRaw): IUser => ({
    id: activateId,
    email: auth0User.email ?? "",
    name: auth0User.name ?? "",
    nickname: auth0User.nickname ?? "",
    picture: auth0User.picture ?? "",
    firstName: data.firstName ?? "",
    lastName: data.lastName ?? "",
  });

  const { data: activateUser } = useActivateUserQuerySelector<IUser>(auth0User, selectUsernameData);
  return activateUser || initialUser;
}
